// Crop a chosen photo, then resize it before upload. Used by every image
// upload (profile photo, provider logo, product image, question images).
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Slider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { type ChangeEvent, type ReactNode, useCallback, useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { useSnackbar } from '@/app/SnackbarProvider';
import { loadImage, renderImage } from '@/lib/images';

export interface ImagePickerOptions {
  /** Fixed shape (width / height). Leave out to let the person pick a shape. */
  aspect?: number;
  /** Round crop guide (profile photos). The saved image is still square. */
  round?: boolean;
  maxSize: number;
  maxBytes: number;
  name?: string;
  title?: string;
}

const FREE_SHAPES: { label: string; value: number | 'original' }[] = [
  { label: 'Original', value: 'original' },
  { label: 'Square', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
];

/**
 * Hook for an `<input type="file">`: opens the crop dialog for the chosen
 * photo and calls onReady with the cropped, resized file. Animated GIFs skip
 * cropping (it would stop the animation) but must fit the size limit.
 */
export function useImagePicker(opts: ImagePickerOptions, onReady: (file: File) => void): { onChange: (e: ChangeEvent<HTMLInputElement>) => void; dialog: ReactNode } {
  const notify = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // choosing the same file again still works
    if (!f) return;
    if (!f.type.startsWith('image/')) return notify('Choose a photo or image file.', 'error');
    if (f.type === 'image/gif') {
      if (f.size > opts.maxBytes) return notify(`GIFs can't be resized. Use one under ${Math.floor(opts.maxBytes / 1_048_576)} MB.`, 'error');
      return onReady(f);
    }
    setFile(f);
  };
  const dialog = file ? (
    <ImageCropDialog
      file={file}
      opts={opts}
      onCancel={() => setFile(null)}
      onDone={(out) => {
        setFile(null);
        onReady(out);
      }}
    />
  ) : null;
  return { onChange, dialog };
}

export function ImageCropDialog({ file, opts, onCancel, onDone }: { file: File; opts: ImagePickerOptions; onCancel: () => void; onDone: (file: File) => void }) {
  const notify = useSnackbar();
  const [url] = useState(() => URL.createObjectURL(file));
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [shape, setShape] = useState<number | 'original'>('original');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    loadImage(url)
      .then((i) => live && setImg(i))
      .catch((e: Error) => {
        notify(e.message, 'error');
        onCancel();
      });
    return () => {
      live = false;
      URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const aspect = opts.aspect ?? (shape === 'original' ? (img ? img.naturalWidth / img.naturalHeight : 1) : shape);
  const onCropComplete = useCallback((_: Area, pixels: Area) => setArea(pixels), []);

  const save = async () => {
    if (!img) return;
    setBusy(true);
    try {
      onDone(await renderImage(img, file.type, area, { maxSize: opts.maxSize, maxBytes: opts.maxBytes, name: opts.name }));
    } catch (e) {
      notify(e instanceof Error ? e.message : "The image couldn't be processed.", 'error');
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="sm">
      <DialogTitle>{opts.title ?? 'Crop photo'}</DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', height: { xs: 280, sm: 360 }, bgcolor: '#0B1530', borderRadius: 1, overflow: 'hidden' }}>
          {img ? (
            <Cropper
              image={url}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={opts.round ? 'round' : 'rect'}
              showGrid={!opts.round}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
            />
          ) : (
            <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress aria-label="Loading photo" />
            </Stack>
          )}
        </Box>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {!opts.aspect && (
            <ToggleButtonGroup exclusive size="small" value={shape} onChange={(_, v) => v !== null && (setShape(v), setZoom(1), setCrop({ x: 0, y: 0 }))} aria-label="Shape">
              {FREE_SHAPES.map((s) => (
                <ToggleButton key={s.label} value={s.value}>
                  {s.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ flexShrink: 0 }}>
              Zoom
            </Typography>
            <Slider value={zoom} min={1} max={4} step={0.05} onChange={(_, v) => setZoom(v as number)} aria-label="Zoom" disabled={!img} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Drag to move{opts.round ? '' : ' the photo inside the frame'}. Big photos are made smaller automatically before upload.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={!img || busy}>
          {busy ? 'Saving…' : 'Use photo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
